import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../Modal';
import type { DocumentRecord } from '../../types';
import { shortFileName } from '../../utils/fileName';

interface AttachmentLibraryPickerProps {
  documents: DocumentRecord[];
  onAdd: (doc: DocumentRecord) => void | Promise<void>;
  onClose: () => void;
}

/** Pick project documents not yet linked to a contract; stacked above the wizard. */
export function AttachmentLibraryPicker({ documents, onAdd, onClose }: AttachmentLibraryPickerProps) {
  const { t } = useTranslation();
  const searchId = useId();
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const visible = needle
    ? documents.filter(doc => (doc.originalName || doc.fileName).toLowerCase().includes(needle))
    : documents;

  return (
    <Modal title={t('contracts.attachments.libraryTitle')} onClose={onClose} zIndex={1060} size="lg">
      <label htmlFor={searchId} className="visually-hidden">{t('contracts.attachments.librarySearch')}</label>
      <input
        id={searchId}
        type="search"
        className="form-control mb-3"
        placeholder={t('contracts.attachments.librarySearch')}
        value={query}
        onChange={event => setQuery(event.target.value)}
      />
      {visible.length === 0 ? (
        <p className="text-muted mb-0">{t('contracts.attachments.libraryEmpty')}</p>
      ) : (
        <ul className="list-unstyled d-flex flex-column gap-2 mb-0">
          {visible.map(doc => (
            <li key={doc.id} className="contract-wizard-file">
              <span className="contract-wizard-file-icon" aria-hidden="true">
                <i className="feather-file-text"></i>
              </span>
              <span className="contract-wizard-file-name">
                <span className="d-block text-truncate fw-medium" title={doc.originalName || doc.fileName}>
                  {shortFileName(doc.originalName || doc.fileName)}
                </span>
              </span>
              <button type="button" className="btn contract-wizard-btn-outline" onClick={() => void onAdd(doc)}>
                {t('contracts.attachments.addAttachment')}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

export default AttachmentLibraryPicker;
